/**
 * アカウント設定。
 *
 * 契約情報（組織名・連絡先）と、ログインしている本人のパスワード・外観設定。
 * メールアドレスのパスワードとは別物なので、その区別を必ず書く
 * （「パスワードを変えたのにメールソフトが繋がらない」の元になる）。
 */
import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  Input,
  KeyValue,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  THEME_PREFERENCES,
  THEME_PREFERENCE_LABELS,
  useTheme,
  type ThemePreference,
} from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useOrg } from '../lib/hooks.js';
import { AppLink } from '../lib/nav.js';
import { Async } from '../components/async.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { HelpNote } from '../components/help.js';
import { changePassword, requestEmailChange, updateProfile } from '../lib/auth.js';
import { useAuth, canManageOrg } from '../stores/auth.js';
import { env } from '../env.js';

export function SettingsPage() {
  const { user } = useAuth();
  const org = useOrg();

  return (
    <>
      <PageHeader title="アカウント設定" description="ご契約情報とログイン情報の設定です。" />

      <div className="space-y-6">
        <Section title="ご契約情報">
          <Async query={org} lines={4}>
            {(data) => <OrgForm orgName={data.displayName} billingEmail={data.billingEmail} />}
          </Async>
        </Section>

        <Section
          title="請求先情報"
          description="請求書の宛先になります。未入力のまま月次の締めが回ると、宛名しか無い請求書が発行されます。"
        >
          <Async query={org} lines={5}>
            {(data) => <BillingProfileForm org={data} />}
          </Async>
        </Section>

        <Section title="ログイン情報">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">あなたのアカウント</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <KeyValue
                items={[
                  { key: 'メールアドレス', value: user?.email ?? '—' },
                  {
                    key: '2 段階認証',
                    value: user?.mfaEnabled ? '有効' : '未設定',
                  },
                ]}
              />
              <DisplayNameForm />
              <Button asChild variant="outline" size="sm">
                <AppLink to="/security">セキュリティ設定を開く</AppLink>
              </Button>
            </CardContent>
          </Card>
        </Section>

        <Section
          title="ログイン用メールアドレスの変更"
          description="コントロールパネルにログインするためのアドレスです。メールの送受信に使うアドレスとは別のものです。"
        >
          <EmailChangeForm />
        </Section>

        <PasswordSection />

        <Section title="画面の表示">
          <ThemeCard />
        </Section>

        {env.supportUrl ? (
          <HelpNote title="お困りのときは">
            <a
              href={env.supportUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-primary underline-offset-4 hover:underline"
            >
              サポート窓口
            </a>
            までお問い合わせください。お問い合わせの際は、ご契約者名とドメイン名をお知らせいただくとスムーズです。
          </HelpNote>
        ) : null}
      </div>
    </>
  );
}

/**
 * 自分の表示名（`PATCH /auth/portal/me`）。
 *
 * **これが無いあいだ、招待時に管理者が打った表示名（誤字を含む）を
 * 本人が生涯直せなかった。** その名前はメンバー一覧・監査ログ・
 * サポート起票に出続ける。API は先に在ったのに `authApi.patch` を
 * 呼ぶ箇所が 1 つも無く、この画面は KeyValue で読み取り専用に
 * 出しているだけだった。
 *
 * **代理ログイン中はサーバが 403 を返す**（運営者が契約者になりすました
 * 状態で書き換えると、監査には「本人が変えた」に見える行が残るため）。
 * 押してから断られるのは体験として最悪なので、先に無効化して理由を出す。
 */
function DisplayNameForm() {
  const { user } = useAuth();
  const [name, setName] = React.useState(user?.displayName ?? '');
  React.useEffect(() => {
    setName(user?.displayName ?? '');
  }, [user?.displayName]);

  const impersonated = user?.impersonated ?? false;

  const save = useMutation({
    mutationFn: () => updateProfile(name.trim()),
  });

  const invalid = name.trim().length === 0 || name.trim().length > 120;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!invalid) save.mutate();
      }}
    >
      <FormAlert error={save.error} />
      {save.isSuccess ? (
        <Alert variant="success">
          <AlertDescription>表示名を変更しました。</AlertDescription>
        </Alert>
      ) : null}

      <Field
        label="表示名"
        htmlFor="me-display-name"
        required
        hint={
          impersonated
            ? '運営スタッフによる代理操作中は変更できません。'
            : 'メンバー一覧や操作の履歴に表示される名前です。'
        }
      >
        <Input
          id="me-display-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={impersonated}
          maxLength={120}
          autoComplete="name"
        />
      </Field>

      {!impersonated ? (
        <SubmitBar
          submitLabel="表示名を変更する"
          pending={save.isPending}
          disabled={invalid || name.trim() === (user?.displayName ?? '')}
        />
      ) : null}
    </form>
  );
}

/**
 * ログインメールアドレスの変更申請（`POST /auth/portal/me/email`）。
 *
 * サーバは **202** を返し、新アドレスへ確認リンク（`/confirm-email?token=...`）を
 * 送る。**その場では変わらない**ことを画面に必ず書く（変わったつもりで
 * ログアウトすると、確定していないので新アドレスでは入れない）。
 *
 * **使用中のアドレスでも同じ 202 が返る**（確認メールだけ送らない）。
 * ここで 409 を返すと「そのアドレスの利用者が居るか」を総当たりで
 * 調べられるため、というサーバ側の判断。だから画面も
 * 「そのアドレスは使われています」とは言えない。
 */
function EmailChangeForm() {
  const { user } = useAuth();
  const [newEmail, setNewEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const impersonated = user?.impersonated ?? false;

  const submit = useMutation({
    mutationFn: () => requestEmailChange(newEmail.trim(), password),
    onSuccess: () => {
      setPassword('');
    },
  });

  const invalid = !/^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/.test(newEmail.trim()) || password.length === 0;

  return (
    <Card>
      <CardContent className="p-5">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!invalid) submit.mutate();
          }}
        >
          <FormAlert error={submit.error} />
          {submit.isSuccess ? (
            <Alert variant="info">
              <AlertTitle>確認メールをお送りしました</AlertTitle>
              <AlertDescription>
                {`${submit.data.pendingEmail} 宛の確認メールにあるリンクを開くと、変更が完了します。`}
                リンクを開くまで、ログインには今までのアドレスをお使いください。
                （そのアドレスが既にご利用中の場合、確認メールは届きません。）
              </AlertDescription>
            </Alert>
          ) : null}

          {impersonated ? (
            <Alert variant="warning">
              <AlertTitle>代理操作中は変更できません</AlertTitle>
              <AlertDescription>
                運営スタッフによる代理操作中は、ログイン用メールアドレスを変更できません。
              </AlertDescription>
            </Alert>
          ) : null}

          <Field
            label="新しいメールアドレス"
            htmlFor="me-email"
            required
            hint="このアドレス宛に確認メールをお送りします。受け取れるアドレスをご指定ください。"
          >
            <Input
              id="me-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={impersonated}
              autoComplete="email"
            />
          </Field>

          <Field
            label="現在のパスワード"
            htmlFor="me-email-pw"
            required
            hint="本人確認のため、コントロールパネルのパスワードを入力してください。"
          >
            <Input
              id="me-email-pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={impersonated}
              autoComplete="current-password"
            />
          </Field>

          <Alert variant="info">
            <AlertTitle>変更が完了すると、ほかの端末のログインは無効になります</AlertTitle>
            <AlertDescription>
              確認リンクを開いた時点で、これまでのログインはすべて切断されます。新しいアドレスでログインし直してください。
            </AlertDescription>
          </Alert>

          {!impersonated ? (
            <SubmitBar
              submitLabel="確認メールを送る"
              pending={submit.isPending}
              disabled={invalid}
            />
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

function OrgForm({ orgName, billingEmail }: { orgName: string; billingEmail: string }) {
  const { user } = useAuth();
  const canWrite = canManageOrg(user);
  const queryClient = useQueryClient();
  const [name, setName] = React.useState(orgName);
  const [email, setEmail] = React.useState(billingEmail);

  const save = useMutation({
    // API のリクエストキーは `displayName` / `billingEmail`。
    // `name` / `contactEmail` で送っても未知キーとして黙って捨てられる。
    mutationFn: () =>
      api.patch<{ ok: boolean }>('/org', { displayName: name, billingEmail: email }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.org() }),
  });

  return (
    <Card>
      <CardContent className="p-5">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <FormAlert error={save.error} />
          {save.isSuccess ? (
            <Alert variant="success">
              <AlertDescription>保存しました。</AlertDescription>
            </Alert>
          ) : null}

          <Field label="ご契約者名（組織名）" htmlFor="org-name" required>
            <Input
              id="org-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canWrite}
            />
          </Field>

          <Field
            label="連絡先メールアドレス"
            htmlFor="org-email"
            required
            hint="障害情報・請求のお知らせをこのアドレスへお送りします。本サービス以外のアドレスを推奨します（障害時に受け取れなくなるため）。"
          >
            <Input
              id="org-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!canWrite}
            />
          </Field>

          {canWrite ? <SubmitBar submitLabel="保存する" pending={save.isPending} /> : null}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * 請求先情報（請求書の宛先）。
 *
 * **請求書 PDF の「宛先」ブロックはこの 4 項目で組み立てている。**
 * 住所・担当者名が空のまま締めが回ると、宛名しか無い請求書が交付され、
 * 受け取った側の経理で必ず差し戻される。入力欄がどこにも無かったので、
 * 契約者には埋める手段自体が存在しなかった。
 *
 * 更新は `PATCH /portal/org`（`GET /portal/billing/subscription` の
 * `billingProfile` が読むのと同じ列）。**キー名は API と 1 文字も違えられない**
 * （未知キーは黙って捨てられるため、間違えると「保存したのに変わらない」になる）。
 */
function BillingProfileForm({
  org,
}: {
  org: {
    postalCode: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    contactName: string | null;
    contactPhone: string | null;
    taxId: string | null;
  };
}) {
  const { user } = useAuth();
  const canWrite = canManageOrg(user);
  const queryClient = useQueryClient();

  const [postalCode, setPostalCode] = React.useState(org.postalCode ?? '');
  const [addressLine1, setAddressLine1] = React.useState(org.addressLine1 ?? '');
  const [addressLine2, setAddressLine2] = React.useState(org.addressLine2 ?? '');
  const [contactName, setContactName] = React.useState(org.contactName ?? '');
  const [contactPhone, setContactPhone] = React.useState(org.contactPhone ?? '');
  const [taxId, setTaxId] = React.useState(org.taxId ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ ok: boolean }>('/org', {
        // 空文字は「未入力」なので null で送る（空文字を入れると
        // `complete` の判定が通ってしまい、宛先が空の請求書が出る）。
        postalCode: postalCode.trim() || null,
        addressLine1: addressLine1.trim() || null,
        addressLine2: addressLine2.trim() || null,
        contactName: contactName.trim() || null,
        contactPhone: contactPhone.trim() || null,
        taxId: taxId.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.org() });
      void queryClient.invalidateQueries({ queryKey: qk.subscription() });
    },
  });

  return (
    <Card>
      <CardContent className="p-5">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <FormAlert error={save.error} />
          {save.isSuccess ? (
            <Alert variant="success">
              <AlertDescription>保存しました。</AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="郵便番号" htmlFor="bp-postal" hint="例: 100-0001">
              <Input
                id="bp-postal"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                autoComplete="postal-code"
                disabled={!canWrite}
              />
            </Field>
            <Field label="ご担当者名" htmlFor="bp-contact" hint="請求書の「ご担当者」欄に記載します。">
              <Input
                id="bp-contact"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                autoComplete="name"
                disabled={!canWrite}
              />
            </Field>
          </div>

          <Field label="住所" htmlFor="bp-addr1">
            <Input
              id="bp-addr1"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              autoComplete="address-line1"
              disabled={!canWrite}
            />
          </Field>

          <Field label="建物名・部屋番号など（任意）" htmlFor="bp-addr2">
            <Input
              id="bp-addr2"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              autoComplete="address-line2"
              disabled={!canWrite}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="お電話番号" htmlFor="bp-phone">
              <Input
                id="bp-phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                autoComplete="tel"
                disabled={!canWrite}
              />
            </Field>
            <Field
              label="適格請求書発行事業者登録番号（任意）"
              htmlFor="bp-taxid"
              hint="お客様側の登録番号です。入力いただくと請求書に併記します。例: T1234567890123"
            >
              <Input
                id="bp-taxid"
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
                disabled={!canWrite}
              />
            </Field>
          </div>

          {canWrite ? <SubmitBar submitLabel="保存する" pending={save.isPending} /> : null}
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordSection() {
  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');

  const mut = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
    },
  });

  const mismatch = confirm.length > 0 && confirm !== next;

  return (
    <Section title="コントロールパネルのパスワード変更">
      <Card>
        <CardContent className="p-5">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!mismatch && next.length >= 8) mut.mutate();
            }}
          >
            <Alert variant="info">
              <AlertTitle>メールのパスワードとは別です</AlertTitle>
              <AlertDescription>
                ここで変更するのは、この管理画面にログインするためのパスワードです。
                メールソフトに設定するパスワードは「メールアドレス」の画面から変更してください。
              </AlertDescription>
            </Alert>

            <FormAlert error={mut.error} />
            {mut.isSuccess ? (
              <Alert variant="success">
                <AlertTitle>変更しました</AlertTitle>
                <AlertDescription>
                  安全のため、他の端末のログインはすべて無効になりました。
                </AlertDescription>
              </Alert>
            ) : null}

            <Field label="現在のパスワード" htmlFor="pw-current" required>
              <Input
                id="pw-current"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
              />
            </Field>
            <Field label="新しいパスワード" htmlFor="pw-next" required hint="8 文字以上">
              <Input
                id="pw-next"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field
              label="新しいパスワード（確認）"
              htmlFor="pw-confirm"
              required
              error={mismatch ? 'パスワードが一致しません' : undefined}
            >
              <Input
                id="pw-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </Field>

            <SubmitBar
              submitLabel="パスワードを変更する"
              pending={mut.isPending}
              disabled={current.length === 0 || next.length < 8 || mismatch}
            />
          </form>
        </CardContent>
      </Card>
    </Section>
  );
}

function ThemeCard() {
  const { preference, setTheme } = useTheme();
  return (
    <Card>
      <CardContent className="p-5">
        <Field label="配色" className="max-w-xs" hint="この端末のブラウザにのみ保存されます。">
          <Select value={preference} onValueChange={(v) => setTheme(v as ThemePreference)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {THEME_PREFERENCES.map((t) => (
                <SelectItem key={t} value={t}>
                  {THEME_PREFERENCE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </CardContent>
    </Card>
  );
}
